import { useRef } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import LanguageToggleButton from '../Common/LanguageToggleButton';
import { Link as ScrollLink } from 'react-scroll'
import { FaPhone, FaWhatsapp, FaEnvelope } from 'react-icons/fa';
import { BsFacebook, BsInstagram, BsLinkedin } from 'react-icons/bs';
import { FaXTwitter } from "react-icons/fa6";
import { BiBuilding, BiTime } from 'react-icons/bi';
import { MdEmail, MdLocationOn, MdConstruction } from 'react-icons/md';

const SOCIAL_ICONS = {
  whatsapp: { Icon: FaWhatsapp, hoverBg: 'hover:bg-green-800', iconColor: 'text-green-800' },
  twitter: { Icon: FaXTwitter, hoverBg: 'hover:bg-black', iconColor: 'text-stone-600' },
  instagram: { Icon: BsInstagram, hoverBg: 'hover:bg-[#E4405F]', iconColor: 'text-stone-600' },
  linkedin: { Icon: BsLinkedin, hoverBg: 'hover:bg-blue-600', iconColor: 'text-stone-600' },
  facebook: { Icon: BsFacebook, hoverBg: 'hover:bg-blue-600', iconColor: 'text-stone-600' }
};

const SocialIcon = ({ platform, url, title }) => {
  const { Icon, hoverBg, iconColor } = SOCIAL_ICONS[platform];
  return (
    <a href={url} 
       title={title} 
       className={`p-3 rounded-full transition-colors duration-300 group bg-stone-100 ${hoverBg}`}>
      <Icon className={`w-5 h-5 ${iconColor} transition-colors duration-300 group-hover:text-white`} />
    </a>
  );
};

const HeaderMobile = ({
	isOpen,
	setIsOpen,
	isScrolled,
	site_info,
	navigation,
	contact_info,
	social_media,
	handleLogoClick,
	handleNavigation,
	activeSection,
	isRTL,
	t,
	scrollConfig,
	handleSetActive,
	LinkContent
}) => {
	const mobileMenuRef = useRef(null);
	const NavigationItem = ({ item, className }) => {
		const location = useLocation();
		const isHomePage = location.pathname === '/';

		if (item.to && isHomePage) {
			return (
				<ScrollLink
					to={item.to}
					href={`/#${item.to}`} // Add href for crawlability
					{...scrollConfig}
					onSetActive={() => handleSetActive(item.to)}
					className={className}
					onClick={() => handleNavigation(item)}
					role="link"
					tabIndex={0}
				>
					<LinkContent item={item} />
				</ScrollLink>
			);
		}

		return (
			<RouterLink
				to={item.href || '/'}
				href={item.href || '/'} // Ensure href is always present
				onClick={() => handleNavigation(item)}
				className={className}
				role="link"
				aria-current={activeSection === item.to || activeSection === item.href ? 'page' : undefined}
			>
				<LinkContent item={item} />
			</RouterLink>
		);
	};

	const renderSocialIcons = () => (
		<div className="flex flex-wrap gap-4 px-4 pt-4 border-t">
		  {Object.entries(social_media).map(([platform, url]) => (
			url && (
			  <SocialIcon
				key={platform}
				platform={platform}
				url={url}
				title={t(`contact.social.${platform}`)}
			  />
			)
		  ))}
		</div>
	  );

	return (
		<div className="lg:hidden">
			<nav className="container p-3 py-3 pt-6 mx-auto">
				<div className="flex justify-between items-center">
					<RouterLink
						to="/"
						onClick={handleLogoClick}
						className="text-2xl font-bold cursor-pointer"
						aria-label={site_info.site_name || "Home"}
					>
						<img src={site_info.logo} alt="Logo" className="h-16" />
					</RouterLink>

					<div className="flex gap-2 items-center">
						<LanguageToggleButton className="lg:hidden" Location="Top" isMobileView={true} />
						<button className={`lg:hidden p-2 rounded-md transition-all duration-300 ease-in-out transform 
							${!isScrolled ? 'text-white' : 'text-[var(--primary-color)]'} 
							${isOpen ? 'scale-95 rotate-180' : 'scale-100 rotate-0'} hover:bg-black/10 active:scale-90 `}
							onClick={() => setIsOpen(!isOpen)}
							aria-label="Toggle menu">
							<div className="relative w-6 h-6">
								<span className={`absolute left-0 block w-full h-0.5 transform transition-all duration-300 ease-in-out
							${!isScrolled ? 'bg-white' : 'bg-[var(--primary-color)]'}
							${isOpen ? 'rotate-45 top-3' : 'rotate-0 top-1'}
							`}></span>
								<span className={`absolute left-0 block w-full h-0.5 top-3 transform transition-all duration-300 ease-in-out
							${!isScrolled ? 'bg-white' : 'bg-[var(--primary-color)]'}
							${isOpen ? 'opacity-0' : 'opacity-100'}
							`}></span>
								<span className={`absolute left-0 block w-full h-0.5 transform transition-all duration-300 ease-in-out
							${!isScrolled ? 'bg-white' : 'bg-[var(--primary-color)]'}
							${isOpen ? '-rotate-45 top-3' : 'rotate-0 top-5'}`}></span>
							</div>
						</button>
					</div>
				</div>
			</nav>

			{/* Mobile Menu Overlay */}
			<div className={`fixed inset-0 z-50 lg:hidden transition-all duration-500 ease-in-out ${isOpen ? 'visible opacity-100' : 'invisible opacity-0'}`}>
				<div className={`fixed inset-0 backdrop-blur-sm bg-black/50 transition-opacity duration-500 ${isOpen ? 'opacity-100' : 'opacity-0'}`} onClick={() => setIsOpen(false)} />
				<div ref={mobileMenuRef} className={` fixed inset-y-0 w-[280px] bg-white shadow-xl transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] 
				${isRTL ? 'right-0' : 'left-0'} 
				${isOpen ? `transform-none opacity-100 ${isRTL ? 'translate-x-0' : 'translate-x-0'}`
						: `opacity-95 ${isRTL ? 'translate-x-full' : '-translate-x-full'}`}`}>
					<div className={` flex flex-col h-full transform transition-transform duration-500 ease-out ${isOpen ? 'scale-100' : 'scale-95'} `}>
						<div className="p-4 border-b">
							<div className="flex justify-between items-center">
								<img src={site_info.logo_dark} alt="Logo" className="h-8" />
								<button onClick={() => setIsOpen(false)} className="p-2 text-gray-600 rounded-full hover:text-gray-900 hover:bg-gray-100" aria-label="Close menu">
									<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
									</svg>
								</button>
							</div>
						</div>

						<div className="overflow-y-auto flex-1 py-4">
							<div className="px-4 space-y-1">
								{navigation.map((item) => (
									<NavigationItem
										key={item.id}
										item={item}
										className={`flex items-center gap-3 w-full px-4 py-3 text-gray-700 rounded-lg transition-colors hover:bg-gray-50 hover:text-stone-800
										${isRTL ? 'text-arabic' : 'text-english'}
										${activeSection === item.to ? 'text-stone-800' : ''}`}
									/>
								))}
							</div>
							<div className="px-4 mt-6">
								<LanguageToggleButton className="lg:hidden" Location="Menu" isMobileView={true} />
							</div>
						</div>
						<div className="px-4 py-6 space-y-4 border-t">
							<div className="space-y-3">
								<a href={`tel:${contact_info?.phone?.replace(/\s+/g, '')}`}
									title={t('common.callUs')}
									className="flex gap-3 items-center px-4 py-2 text-gray-600 rounded-lg hover:text-stone-800 hover:bg-gray-50">
									<FaPhone className="w-4 h-4" />
									<span dir="ltr" className="text-sm">{contact_info.phone}</span>
								</a>
								<a href={`mailto:${contact_info.email}`}
									title={t('common.emailUs')}
									className="flex gap-3 items-center px-4 py-2 text-gray-600 rounded-lg hover:text-stone-800 hover:bg-gray-50">
									<FaEnvelope className="w-4 h-4" />
									<span dir="ltr" className="text-sm break-all">{contact_info.email}</span>
								</a>
							</div>
							{renderSocialIcons()}
						</div>
					</div>
				</div>
			</div>
		</div >
	);
};

export default HeaderMobile;